import { MixMatchComponent } from "./mix-and-match-component";
import { FetchModalProductPopUp } from "../../g-modal/g-modal";

interface Variant {
  id: string | number;
  title: string;
  price: number;
  available: boolean;
  options?: string[];
  image?: string | { src: string };
  featured_image?: string | { src: string };
  handle?: string;
  product_type?: string;
  inventory_quantity?: number;
  inventory_management?: string;
  inventory_policy?: string;
  material?: string;
}

interface DropdownElements {
  triggerEl: HTMLElement | null;
  menuEl: HTMLElement | null;
  optionEls: NodeListOf<Element>;
}

class MMProductCardDropdown {
  productCard: MMProductCard;
  optionsSelectorsEls: NodeListOf<Element>;

  constructor(productCard: MMProductCard) {
    this.productCard = productCard;
    this.optionsSelectorsEls = productCard.optionsSelectorsEls;
  }

  init(): void {
    this._initializeDropdowns();
    this._setupOutsideClickHandler();
    this.updateDropdownOptionsState();
  }

  _getDropdownElements(selector: Element): DropdownElements {
    return {
      triggerEl: selector.querySelector('.variant-dropdown-trigger') as HTMLElement | null,
      menuEl: selector.querySelector('.variant-dropdown-menu') as HTMLElement | null,
      optionEls: selector.querySelectorAll('.dropdown-option')
    };
  }

  _initializeDropdowns(): void {
    this.optionsSelectorsEls.forEach((selector) => {
      const { triggerEl, menuEl, optionEls } = this._getDropdownElements(selector);
      if (!triggerEl || !menuEl) {
        return;
      }

      // Toggle dropdown on trigger click
      triggerEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._toggleDropdown(triggerEl, menuEl);
      });

      // Keyboard navigation
      triggerEl.addEventListener('keydown', (e) => {
        this._handleDropdownKeyNavigation(e, triggerEl, menuEl, optionEls);
      });

      // Handle option selection
      optionEls.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (option.classList.contains('unavailable')) {
            return;
          }

          this._selectDropdownOption(selector, option as HTMLElement, triggerEl, menuEl);
        });
      });
    });
  }

  _setupOutsideClickHandler(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target && target.closest && !target.closest('.mix-and-match-variant-selector')) {
        this._closeAllCustomDropdowns();
      }
    });
  }

  _toggleDropdown(triggerEl: HTMLElement, menuEl: HTMLElement): void {
    const isOpen = triggerEl.getAttribute('aria-expanded') === 'true';

    // Close all other dropdowns first
    this._closeAllCustomDropdowns();

    if (!isOpen) {
      triggerEl.setAttribute('aria-expanded', 'true');
      menuEl.classList.remove('tw-hidden');
      triggerEl.focus();
    }
  }

  _closeAllCustomDropdowns(): void {
    this.optionsSelectorsEls.forEach((selector) => {
      const { triggerEl, menuEl } = this._getDropdownElements(selector);

      if (triggerEl && menuEl) {
        triggerEl.setAttribute('aria-expanded', 'false');
        menuEl.classList.add('tw-hidden');
      }
    });
  }

  _selectDropdownOption(selector: Element, option: HTMLElement, triggerEl: HTMLElement, menuEl: HTMLElement): void {
    const value = option.dataset.value || option.getAttribute('data-value') || '';
    const selectedValueEl = triggerEl.querySelector('.selected-value') as HTMLElement | null;
    if (!selectedValueEl) return;

    // Update selected display
    selectedValueEl.textContent = value;
    selectedValueEl.dataset.value = value;

    // Update the image
    const optionImg = option.querySelector('img') as HTMLImageElement | null;
    const triggerImg = triggerEl.querySelector('img') as HTMLImageElement | null;
    if (optionImg && triggerImg) {
      // Copy all image attributes from the option to the trigger
      triggerImg.src = optionImg.src;
      if (optionImg.srcset) {
        triggerImg.srcset = optionImg.srcset;
      }
      if (optionImg.alt) {
        triggerImg.alt = optionImg.alt;
      }
    }

    // Update option states
    const allOptionEls = selector.querySelectorAll('.dropdown-option');
    allOptionEls.forEach(opt => {
      opt.classList.remove('selected');
      opt.setAttribute('aria-selected', 'false');
    });
    option.classList.add('selected');
    option.setAttribute('aria-selected', 'true');

    // Close dropdown
    triggerEl.setAttribute('aria-expanded', 'false');
    menuEl.classList.add('tw-hidden');

    // Sync color swatches with dropdown selection
    this._syncSwatchesWithDropdown(value);

    // Trigger variant change
    this._handleVariantChange();
  }

  _syncSwatchesWithDropdown(value: string): void {
    // Update color swatch selected states to match dropdown
    const colorSwatchEls = this.productCard.colorSwatchEls;
    if (!colorSwatchEls) return;

    colorSwatchEls.forEach((swatch) => {
      const swatchEl = swatch as HTMLElement;
      const isSelected = swatchEl.dataset.value === value;
      swatchEl.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      
      // Update border styling
      if (isSelected) {
        swatchEl.classList.remove('tw-border-transparent', 'hover:tw-border-brightGray');
        swatchEl.classList.add('tw-border-maastrichtBlue');
      } else {
        swatchEl.classList.remove('tw-border-maastrichtBlue');
        swatchEl.classList.add('tw-border-transparent', 'hover:tw-border-brightGray');
      }
    });
  }

  _handleDropdownKeyNavigation(e: KeyboardEvent, triggerEl: HTMLElement, menuEl: HTMLElement, optionEls: NodeListOf<Element>): void {
    const isOpen = triggerEl.getAttribute('aria-expanded') === 'true';

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this._toggleDropdown(triggerEl, menuEl);
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          this._closeAllCustomDropdowns();
          triggerEl.focus();
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (isOpen) {
          e.preventDefault();
          this._navigateDropdownOptions(optionEls, e.key === 'ArrowDown' ? 1 : -1);
        }
        break;
    }
  }

  _navigateDropdownOptions(optionEls: NodeListOf<Element>, direction: number): void {
    const availableOptionEls = Array.from(optionEls).filter(opt => !opt.classList.contains('unavailable'));
    const currentHighlightedEl = availableOptionEls.find(opt => opt.classList.contains('is-hovered'));

    // Remove current highlight
    availableOptionEls.forEach(opt => opt.classList.remove('is-hovered'));

    // Calculate next index
    let nextIndex = 0;
    if (currentHighlightedEl) {
      const currentIndex = availableOptionEls.indexOf(currentHighlightedEl);
      nextIndex = (currentIndex + direction + availableOptionEls.length) % availableOptionEls.length;
    }

    // Highlight next option
    const nextOptionEl = availableOptionEls[nextIndex];
    if (nextOptionEl) {
      nextOptionEl.classList.add('is-hovered');
      (nextOptionEl as HTMLElement).focus();
    }
  }

  _handleVariantChange(): void {
    const selectedOptions: string[] = [];

    // Get selected values from custom dropdowns
    this.optionsSelectorsEls.forEach((selector) => {
      const selectedValueEl = selector.querySelector('.selected-value') as HTMLElement | null;
      if (selectedValueEl && selectedValueEl.dataset.value) {
        selectedOptions.push(selectedValueEl.dataset.value);
      }
    });

    // Find matching variant/product
    const variantsToSearch = this.productCard.getFoxsellVariants();

    // When working with products from collections, we match by product title
    const matchingVariant = variantsToSearch.find((variant) => {
      // Try matching by product title first (for collection-based products)
      if (selectedOptions.length === 1) {
        return variant.title === selectedOptions[0];
      }
      
      // Fallback to traditional option matching
      if (variant.options) {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      }
      
      return false;
    });

    if (matchingVariant) {
      this.productCard.switchToVariant(matchingVariant);
      this.updateDropdownOptionsState();
      this.productCard.updateQuantityDisplay();
    } else {
      console.error("No variant could be matched for selected options", selectedOptions);
    }
  }

  updateDropdownOptionsState(): void {
    // Get current selections
    const currentSelections = this._getCurrentSelections();
    this.optionsSelectorsEls.forEach((selector) => {
      const optionEls = selector.querySelectorAll('.dropdown-option');
      const optionPosition = parseInt((selector as HTMLElement).dataset.optionPosition || '1', 10) - 1;

      optionEls.forEach((option) => {
        const value = (option as HTMLElement).dataset.value || '';

        // Create a test selection array with this option value
        const testSelections = [...currentSelections];
        testSelections[optionPosition] = value;
        // Check if any variant exists with this combination
        const isAvailable = this._isOptionCombinationAvailable(testSelections);

        option.classList.remove('available', 'unavailable');
        option.classList.add(isAvailable ? 'available' : 'unavailable');
        option.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
      });
    });
  }

  _getCurrentSelections(): string[] {
    const selections: string[] = [];

    this.optionsSelectorsEls.forEach((selector) => {
      const selectedValueEl = selector.querySelector('.selected-value') as HTMLElement | null;
      if (selectedValueEl && selectedValueEl.dataset.value) {
        selections.push(selectedValueEl.dataset.value);
      }
    });

    return selections;
  }

  _isOptionCombinationAvailable(testSelections: string[]): boolean {
    const variantsToSearch = this.productCard.getFoxsellVariants();
    
    // When working with products from collections, testSelections contains product titles
    // We need to check if the selection matches any available variant's title
    return variantsToSearch.some((variant) => {
      if (!variant.available) {
        return false;
      }

      // Check if this is a product-based selection (testSelections contains product titles)
      // or a traditional variant-based selection (testSelections contains option values)
      if (testSelections.length === 1 && testSelections[0]) {
        // Single selection - match by product title
        const matchesTitle = variant.title === testSelections[0];
        if (matchesTitle) {
          return true;
        }
        
        // Also check traditional variant options if they exist
        if (variant.options && variant.options.length > 0) {
          return variant.options[0] === testSelections[0];
        }
        
        return false;
      }
      
      // Traditional multi-option variant check
      if (!variant.options) {
        return false;
      }

      return variant.options.every((option, index) => {
        if (testSelections[index] === undefined) return true;
        return option === testSelections[index];
      });
    });
  }
}

export class MMProductCard extends MixMatchComponent {
  static observedAttributes = ["qnt"];

  category: string;
  variantId: string | number;
  productId: string | undefined;
  title!: string;
  image: string | undefined;
  handle: string | undefined;
  productType: string;
  availableVariants: Variant[];
  foxsellVariants: Variant[];
  defaultVariant: Variant | null;
  currentVariant: Variant | null;
  originalPrice: number;
  quantityValue: number;
  variantKey: string;
  displayMode: 'separated' | 'grouped';
  dropdown?: MMProductCardDropdown;
  
  priceEls: NodeListOf<Element>;
  comparePriceEls: NodeListOf<Element>;
  discountEl: Element | null;
  inputEl: HTMLInputElement | null;
  optionsSelectorsEls: NodeListOf<Element>;
  plusEls: NodeListOf<Element>;
  minusEls: NodeListOf<Element>;
  colorSwatchEls: NodeListOf<Element>;
  colorSwatchMoreEl: HTMLElement | null;
  colorSwatchContainer: HTMLElement | null;

  constructor() {
    super();
    // Initialize arrays
    this.availableVariants = [];
    this.foxsellVariants = [];

    // Step 1: Set basic properties (needed by _parseVariantsData to find current variant)
    this.category = this.dataset.category || '';
    this.variantId = this.dataset.mixProduct ? Number(this.dataset.mixProduct) : '';
    this.productId = this.dataset.productId;

    this.title = this.dataset.variantTitle || '';
    this.image = this.dataset.image;
    this.handle = this.dataset.handle;
    this.productType = this.dataset.productType || "";

    // Step 2: Parse variants (needs variantId, populates availableVariants)
    this._parseVariantsData();


    // Step 3: Complete initialization (needs availableVariants for price lookup)
    this._initializeProductData();
    this._cacheUIElements();
    this._init();
  }

  _initializeProductData(): void {
    // Use the pre-calculated Foxsell price from data-original-price attribute first
    // This attribute is set in Liquid with the correct custom price from all_products_category
    const dataOriginalPrice = this.dataset.originalPrice;
    if (dataOriginalPrice && !isNaN(Number(dataOriginalPrice))) {
      this.originalPrice = Number(dataOriginalPrice);
    } else {
      // Fallback to variant lookup if data attribute not set
      this.originalPrice = (this._getVariantPrice(this.variantId) || 0) * 100;
    }
    
    this.quantityValue = 1;
    this.variantKey = `${this.category}_${this.variantId}`;
    this.displayMode = this._detectDisplayMode();
  }

  _parseVariantsData(): void {
    try {
      // Use global Foxsell price map for price overrides (set in mix-and-match.liquid)
      const globalFoxsellPriceMap = (window as any).foxsellPriceMap || {};
      
      // Try to get Shopify product variants first (for full personalization functionality)
      const shopifyVariantsAttr = this.getAttribute('data-shopify-variants');
      let allVariants: any[] = [];

      if (shopifyVariantsAttr) {
        // Use Shopify variants if available (main product cards)
        allVariants = JSON.parse(shopifyVariantsAttr)
          .filter((variant: any) => variant.id != undefined);
        
        // Override prices with Foxsell custom prices if available
        allVariants = allVariants.map((variant: any) => {
          const variantIdStr = String(variant.id);
          const foxsellPrice = globalFoxsellPriceMap[variantIdStr];
          if (foxsellPrice !== undefined) {
            return { ...variant, price: foxsellPrice };
          }
          return variant;
        });
      } else {
        // Fall back to foxsell variants (cart summary mini cards)
        const scriptEl = this.querySelector('script[type="application/json"]') as HTMLScriptElement | null;
        if (scriptEl?.textContent) {
          allVariants = JSON.parse(scriptEl.textContent)
            .filter((variant: any) => variant.id != undefined);
          
          // Override prices with global Foxsell price map if available
          if ((window as any).foxsellPriceMap) {
            allVariants = allVariants.map((variant: any) => {
              const foxsellPrice = (window as any).foxsellPriceMap[String(variant.id)];
              if (foxsellPrice !== undefined) {
                return { ...variant, price: foxsellPrice };
              }
              return variant;
            });
          }
        } else {
          console.warn('No variant data found for component');
          allVariants = [];
        }
      }



      // For cart summary mini cards, use all variants as available (no separation)
      if (!shopifyVariantsAttr) {
        this.availableVariants = allVariants;
        this.defaultVariant = null;
      } else {
        // For main product cards, separate default and personalization variants
        this.defaultVariant = allVariants[0] || null;

        // Skip the first variant for availableVariants (personalization variants only)
        this.availableVariants = allVariants.slice(1);

      }

      // Also parse foxsell variants for backward compatibility (used by dropdown logic)
      const scriptEl = this.querySelector('script[type="application/json"]') as HTMLScriptElement | null;
      if (scriptEl?.textContent) {
        this.foxsellVariants = JSON.parse(scriptEl.textContent)
          .filter((variant: any) => variant.id != undefined);
      } else {
        this.foxsellVariants = [];
      }

      this.currentVariant = this._findCurrentVariant();
    } catch (error) {
      console.error("Error parsing variants:", error);
      this.availableVariants = [];
      this.foxsellVariants = [];
      this.defaultVariant = null;
      this.currentVariant = null;
    }
  }

  _findCurrentVariant(): Variant | null {
    if (!this.variantId) {
      console.debug('No variantId available for lookup');
      return null;
    }

    const foundInAvailable = this.availableVariants.find(variant => {
      const match = variant.id == this.variantId;
      return match;
    });
    if (foundInAvailable) {
      return foundInAvailable;
    }

    if (this.defaultVariant && this.defaultVariant.id == this.variantId) {
      return this.defaultVariant;
    }

    return this.availableVariants[0] || null;
  }

  _cacheUIElements(): void {
    // Price elements
    this.priceEls = this.querySelectorAll(".js-price");
    this.comparePriceEls = this.querySelectorAll(".js-compare-price");
    this.discountEl = this.querySelector(".js-bundle-discount-label");

    // Input elements
    this.inputEl = this.querySelector(".js-qnt-input") as HTMLInputElement | null;
    this.optionsSelectorsEls = this.querySelectorAll(".mix-and-match-variant-selector");

    // Button elements
    this.plusEls = this.querySelectorAll(".js-plus");
    this.minusEls = this.querySelectorAll(".js-minus");
    this.colorSwatchEls = this.querySelectorAll(".js-color-swatch");
    this.colorSwatchMoreEl = this.querySelector(".js-color-swatch-more");
    this.colorSwatchContainer = this.querySelector(".js-color-swatches");
  }

  _detectDisplayMode(): 'separated' | 'grouped' {
    // In separated mode, there will only be one variant in the data
    // In grouped mode, there will be multiple variants
    try {
      const scriptEl = this.querySelector('script[type="application/json"]') as HTMLScriptElement | null;
      if (!scriptEl || !scriptEl.textContent) return 'separated';

      let variants = JSON.parse(scriptEl.textContent).filter((e: any) => e.id != undefined);
      
      // Apply Foxsell price overrides before storing in state
      const globalFoxsellPriceMap = (window as any).foxsellPriceMap || {};
      variants = variants.map((variant: any) => {
        const foxsellPrice = globalFoxsellPriceMap[String(variant.id)];
        if (foxsellPrice !== undefined) {
          return { ...variant, price: foxsellPrice };
        }
        return variant;
      });

      if (variants.length > 1) {
        this._fillStorageFromVariant(variants);
      }
      return variants.length === 1 ? 'separated' : 'grouped';
    } catch (e) {
      console.error("Error detecting display mode:", e);
      return 'grouped'; // Default to grouped mode
    }
  }

  _fillStorageFromVariant(variants: Variant[]): void {
    variants.forEach(variant => {
      // Get image from variant - handle both object {src: "url"} and string "url" formats
      let variantImage = "";
      if (variant.image) {
        variantImage = typeof variant.image === 'string' ? variant.image : variant.image.src;
      } else if (variant.featured_image) {
        variantImage = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
      } else {
        variantImage = this.dataset.image || "";
      }
      
      if (!variantImage) {
        console.warn(`No image found for variant ${variant.id}:`, variant);
      }
      
      // Use variant's own data from JSON, not component's properties
      const variantTitle = variant.title;
      const variantHandle = variant.handle;
      const variantProductType = variant.product_type;
      
      // Check if variant already exists in state with quantity > 0
      // If so, preserve the existing price (it was set correctly by popup with surcharge calculation)
      const variantKey = `${this.category}_${variant.id}`;
      const existingVariant = (window as any).mixMatchStore?.state?.variants?.[variantKey];
      const priceToUse = (existingVariant && existingVariant.quantity > 0) 
        ? existingVariant.price 
        : variant.price;
      
      window.mixMatchStore.setStorageData({
        id: variant.id,
        category: this.category,
        quantity: 0,
        title: variantTitle,
        image: variantImage,
        price: priceToUse,
        type: "product",
        handle: variantHandle,
        inventory_quantity: variant.inventory_quantity,
        product_type: variantProductType,
      });
    });
  }

  _init(): void {
    const state = window.mixMatchStore.state;
    // Don't call _updateStorageQuantity(0) during init to avoid infinite loops
    this._setPrices(state);
    this._setDiscount(state);
    this._updateVariantQuantity();
    this.updateQuantityDisplay();
    if (this.displayMode === 'grouped') {
      this.dropdown = new MMProductCardDropdown(this);
      this.dropdown.init();
    }

    this.plusEls.forEach((el) => {
      el.addEventListener("click", () => {
        if (this.currentVariant && this.currentVariant.available) {
          window.mixMatchStore._setMainCTAText("any_item");
            this._updateStorageQuantity(this.quantityValue);
        }
      });
    });

    this.minusEls.forEach((el) =>
      el.addEventListener("click", () => {
        window.mixMatchStore._setMainCTAText("any_item");
        this._updateStorageQuantity(-this.quantityValue);
      })
    );

    // Color swatch click handlers
    this.colorSwatchEls.forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this._handleColorSwatchClick(el as HTMLElement);
      });
    });

    // Color swatch "show more" button handler
    if (this.colorSwatchMoreEl && this.colorSwatchContainer) {
      this.colorSwatchMoreEl.addEventListener("click", (e) => {
        e.preventDefault();
        this.colorSwatchContainer?.setAttribute('data-expanded', 'true');
      });
    }
  }

  _handleColorSwatchClick(clickedSwatch: HTMLElement): void {
    const value = clickedSwatch.dataset.value;
    if (!value) return;

    // Find matching variant by title
    const matchingVariant = this.foxsellVariants.find((variant) => variant.title === value);

    if (matchingVariant) {
      // Update swatch selected states
      this.colorSwatchEls.forEach((swatch) => {
        const swatchEl = swatch as HTMLElement;
        const isSelected = swatchEl.dataset.value === value;
        swatchEl.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        
        // Update border styling
        if (isSelected) {
          swatchEl.classList.remove('tw-border-transparent', 'hover:tw-border-brightGray');
          swatchEl.classList.add('tw-border-maastrichtBlue');
        } else {
          swatchEl.classList.remove('tw-border-maastrichtBlue');
          swatchEl.classList.add('tw-border-transparent', 'hover:tw-border-brightGray');
        }
      });

      // Also update dropdown if it exists (keep them in sync)
      if (this.dropdown) {
        this._syncDropdownWithSwatch(value);
      }

      // Switch to the variant
      this.switchToVariant(matchingVariant);
      this.updateQuantityDisplay();
    }
  }

  _syncDropdownWithSwatch(value: string): void {
    // Update dropdown's selected value to match swatch selection
    this.optionsSelectorsEls.forEach((selector) => {
      const triggerEl = selector.querySelector('.variant-dropdown-trigger') as HTMLElement | null;
      const selectedValueEl = triggerEl?.querySelector('.selected-value') as HTMLElement | null;
      
      if (selectedValueEl) {
        selectedValueEl.textContent = value;
        selectedValueEl.dataset.value = value;
      }

      // Update dropdown option images to match
      const optionEls = selector.querySelectorAll('.dropdown-option');
      optionEls.forEach((option) => {
        const optEl = option as HTMLElement;
        const isSelected = optEl.dataset.value === value;
        optEl.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        optEl.classList.toggle('selected', isSelected);

        // Copy image from selected option to trigger
        if (isSelected && triggerEl) {
          const optionImg = optEl.querySelector('img') as HTMLImageElement | null;
          const triggerImg = triggerEl.querySelector('img') as HTMLImageElement | null;
          if (optionImg && triggerImg) {
            triggerImg.src = optionImg.src;
            if (optionImg.srcset) triggerImg.srcset = optionImg.srcset;
            if (optionImg.alt) triggerImg.alt = optionImg.alt;
          }
        }
      });
    });

    // Update dropdown options state
    if (this.dropdown) {
      this.dropdown.updateDropdownOptionsState();
    }
  }

  getFoxsellVariants(): Variant[] {
    // Return foxsell variants (collection product variants) for dropdown compatibility
    return this.foxsellVariants;
  }

  switchToVariant(newVariant: Variant): void {
    this.variantId = newVariant.id;
    this.variantKey = `${this.category}_${this.variantId}`;
    this.currentVariant = newVariant;

    if (newVariant.price !== undefined) {
      this.originalPrice = newVariant.price;
    } else {
      this.originalPrice = (this._getVariantPrice(this.variantId) || 0) * 100;
    }

    this.dataset.mixProduct = String(newVariant.id);
    this.dataset.originalPrice = String(newVariant.price);

    if (newVariant.product_type) {
      this.productType = newVariant.product_type;
      this.dataset.productType = newVariant.product_type;
    }

    this.title = newVariant.title;
    this.dataset.variantTitle = newVariant.title;
    this.handle = newVariant.handle;

    // Update modal trigger's variant ID and handle so modal opens with selected product
    const modalTrigger = this.querySelector('modal-trigger') as HTMLElement | null;
    if (modalTrigger) {
      modalTrigger.dataset.variantId = String(newVariant.id);
      if (newVariant.handle) {
        modalTrigger.dataset.contentKey = newVariant.handle;
      }
    }

    this._updateProductMaterial(newVariant);
    this._updateVariantImage(newVariant);

    // Update UI elements for new variant
    this._setPrices(window.mixMatchStore.state);
    this._setDiscount(window.mixMatchStore.state);
    this._updateVariantQuantity();
    this.updateQuantityDisplay();
  }

  _switchToVariantById(variantId: string | number | null): void {
    if (!variantId) return;

    let targetVariant = this.availableVariants.find(variant =>
      variant.id == variantId
    );

    if (!targetVariant && this.defaultVariant && this.defaultVariant.id == variantId) {
      targetVariant = this.defaultVariant;
    }

    if (targetVariant) {
      this.switchToVariant(targetVariant);
    } else {
      console.warn(`Variant with ID ${variantId} not found in available variants or default variant`);
    }
  }

  _updateProductMaterial(variant: Variant): void {
    const materialEl = this.querySelector('.js-product-material') as HTMLElement | null;
    if (!materialEl) {
      console.warn('Product material element not found');
      return;
    }
    
    // Update material if available
    if (variant.material) {
      materialEl.textContent = variant.material;
    }
  }

  _updateVariantImage(variant: Variant): void {
    let imageUrl: string | null = null;
    const imageAlt = variant.title || this.title || '';

    if (variant.featured_image) {
      if (typeof variant.featured_image === 'string') {
        imageUrl = variant.featured_image;
      } else if (variant.featured_image.src) {
        imageUrl = variant.featured_image.src;
      }
    } else if (variant.image) {
      if (typeof variant.image === 'string') {
        imageUrl = variant.image;
      } else if (variant.image.src) {
        imageUrl = variant.image.src;
      }
    }

    if (!imageUrl) {
      return;
    }

    const formattedImageUrl = this._formatShopifyImageUrl(imageUrl, "160x160");

    this.dataset.image = formattedImageUrl;

    const productImageEl = this.querySelector("img") as HTMLImageElement | null;
    if (!productImageEl) {
      console.warn("Image element not found");
      return;
    }
    
    productImageEl.src = formattedImageUrl;
    productImageEl.alt = imageAlt;
    productImageEl.srcset = this._generateImageSrcset(imageUrl);
  }

  _generateImageSrcset(originalUrl: string): string {
    const sizes = ["160x", "320x", "480x", "640x", "800x"];
    return sizes
      .map((size) => {
        const formattedUrl = this._formatShopifyImageUrl(originalUrl, size);
        const width = size.replace("x", "");
        return `${formattedUrl} ${width}w`;
      })
      .join(", ");
  }

  _formatShopifyImageUrl(originalUrl: string, size: string = "160x160"): string {
    try {
      const cleanUrl = originalUrl.split("?")[0];

      if (cleanUrl.includes("cdn.shopify.com") || cleanUrl.includes("shopify.com")) {
        // For Shopify images, use the image transform format
        const urlParts = cleanUrl.split(".");
        const extension = urlParts.pop();
        const baseUrl = urlParts.join(".");
        return `${baseUrl}_${size}.${extension}`;
      }

      return originalUrl;
    } catch (error) {
      console.warn("Error formatting image URL:", error);
      return originalUrl;
    }
  }

  updateQuantityDisplay(): void {
    // Update quantity input to show quantity for currently selected variant
    const currentQuantity = this._getCurrentQuantity();
    const totalProductQuantity = this._getTotalProductQuantity();
    if (this.inputEl) {
      this.inputEl.setAttribute("value", String(currentQuantity));
      this.inputEl.value = String(currentQuantity);
    }
    // Use total product quantity for qnt attribute (includes all variants with same handle)
    this.setAttribute("qnt", String(totalProductQuantity));
  }

  _getCurrentQuantity(): number {
    if (!window.mixMatchStore) return 0;
    const state = window.mixMatchStore.state;
    const variantKey = `${this.category}_${this.variantId}`;
    return state.variants[variantKey] ? state.variants[variantKey].quantity : 0;
  }
  
  _getTotalProductQuantity(): number {
    if (!window.mixMatchStore) return 0;
    const state = window.mixMatchStore.state;
    
    // Get quantity for this specific variant
    const variantQuantity = state.variants[this.variantKey]?.quantity || 0;
    let totalQuantity = variantQuantity;
    
    // Add quantities from all variants with the same handle (product)
    if (this.handle) {
      for (const key of Object.keys(state.variants)) {
        const variant = state.variants[key];
        if (variant.handle === this.handle && key !== this.variantKey) {
          totalQuantity += variant.quantity || 0;
        }
      }
    }
    
    return totalQuantity;
  }

  _getBundleVariantQuantityFromCart(): number {
    let bundleVariantQuantity = 0;
    const cart = window.cachedCart;

    if (!cart?.items) {
      return bundleVariantQuantity;
    }

    cart.items.forEach(item => {
      const bundleItemsProperty = item.properties?.['__foxsell:dynamic_add_on_bundle_items'];
      if (bundleItemsProperty) {
        try {
          const bundleItems = JSON.parse(bundleItemsProperty as string);
          if (Array.isArray(bundleItems)) {
            bundleItems.forEach((bundleItem: any) => {
              if (bundleItem.variantId == this.variantId) {
                bundleVariantQuantity += (bundleItem.quantity || 0) * item.quantity;
              }
            });
          }
        } catch (error) {
          console.warn('Error parsing bundle items:', error);
        }
      }
      if (item.variant_id == this.variantId) {
        bundleVariantQuantity += item.quantity;
      }
    });

    return bundleVariantQuantity;
  }

  _updateVariantQuantity(): void {
    if (this.inputEl && this.currentVariant) {
      // WHY: Respect Shopify inventory settings for proper stock management
      // If inventory tracking is disabled or set to "continue selling", allow unlimited quantity
      let maxInventory = this.currentVariant.inventory_quantity || 0;
      if (this.currentVariant.inventory_management != "shopify" || this.currentVariant.inventory_policy === "continue") {
        maxInventory = 999;
      }

      // WHY: Subtract existing cart quantity to prevent overselling
      // Important for proper inventory management when bundles are already in cart
      const totalInCart = this._getBundleVariantQuantityFromCart();
      const availableMax = Math.max(0, maxInventory - totalInCart);
      this.inputEl.setAttribute("max", String(availableMax));
    }

    if (this.inputEl) {
      const currentValue = parseInt(this.inputEl.getAttribute("value") || '0', 10);
      const maxValue = parseInt(this.inputEl.getAttribute("max") || '999', 10);
      const hasInventoryAvailable = currentValue < maxValue;
      if (this.plusEls.length > 0) {
        this.plusEls.forEach((el) => {
          if (hasInventoryAvailable) {
            el.removeAttribute("disabled");
          }
          else {
            el.setAttribute("disabled", "true");
          }
        });
      }
    }
  }

  _updateStorageQuantity(quantity: number): void {
    // Skip updates if component is not properly initialized
    if (!this.currentVariant || !window.mixMatchStore || !this.variantId) {
      console.debug("Skipping quantity update - component not fully initialized", {
        currentVariant: !!this.currentVariant,
        mixMatchStore: !!window.mixMatchStore,
        variantId: this.variantId,
        quantity: quantity
      });
      return;
    }

    // For removal operations (negative quantity), allow even if variant is not available
    // For addition operations (positive quantity), check availability
    if (quantity > 0 && !this.currentVariant.available) {
      console.warn("Cannot add quantity for unavailable variant", this.currentVariant);
      return;
    }

    // Get image from dataset or current variant
    let variantImage = this.dataset.image || '';
    if (!variantImage) {
      if (this.currentVariant.image) {
        variantImage = typeof this.currentVariant.image === 'string' ? this.currentVariant.image : this.currentVariant.image.src;
      } else if (this.currentVariant.featured_image) {
        variantImage = typeof this.currentVariant.featured_image === 'string' ? this.currentVariant.featured_image : this.currentVariant.featured_image.src;
      }
    }

    if (!variantImage && quantity > 0) {
      console.warn(`No image found when updating quantity for variant ${this.variantId}:`, {
        datasetImage: this.dataset.image,
        currentVariantImage: this.currentVariant.image,
        featuredImage: this.currentVariant.featured_image
      });
    }

    window.mixMatchStore.setStorageData({
      id: this.variantId,
      category: this.category,
      quantity: quantity,
      title: this.title,
      image: variantImage,
      price: this.originalPrice,
      type: "product",
      handle: this.handle,
      inventory_quantity: this.inputEl ? parseInt(this.inputEl.getAttribute("max") || '0', 10) : 0,
      product_type: this.productType,
    });
  }

  _getVariantPrice(variantId: string | number): number | null {
    // First, try to find in availableVariants (this works for ALL products, including collection products)
    if (this.availableVariants && this.availableVariants.length > 0) {
      const variant = this.availableVariants.find(v => v.id == variantId);
      if (variant && variant.price !== undefined) {
        // Prices in the JSON are in cents, convert to dollars for the *100 multiplication later
        return variant.price / 100;
      }
    }

    // Fallback to mmConfig (only for FoxSell-configured products)
    if (window.mmConfig && window.mmConfig.product_properties) {
      for (const productKey in window.mmConfig.product_properties) {
        const product = window.mmConfig.product_properties[productKey];
        if (product.variants && product.variants[`gid://shopify/ProductVariant/${variantId}`]) {
          return product.variants[`gid://shopify/ProductVariant/${variantId}`];
        }
      }
    }

    console.warn(`Variant price not found for ID: ${variantId}. Check availableVariants and mmConfig.`);
    return null;
  }

  // update percentage and price on product card
  _setPrices(state: any): void {
    if (!window.mixMatchStore) {
      console.warn("Cannot update prices. Check mixMatchStore");
      return;
    }
    const variantKey = `${this.category}_${this.variantId}`;

    if (!state.variants[variantKey]) {
      // Variant not in state yet - this is normal during initial load
      return;
    }

    const priceFromStorage = Math.ceil(state.variants[variantKey].price);
    const discount = state.currentDiscount || 0;
    const price = Math.ceil(priceFromStorage * (1 - discount / 100));
    const comparePrice = priceFromStorage;
    this.priceEls.forEach((el) => {
      el.setAttribute("data-price", window.formatMoney(price, window.moneyFormat));
      if (el.hasAttribute("data-price-total")) {
        el.setAttribute("data-price-total", window.formatMoney(price * state.variants[variantKey].quantity, window.moneyFormat));
      }
    });
    this.comparePriceEls.forEach((el) => {
      if (price == comparePrice) {
        el.setAttribute("data-compare-price", "0");
        if (el.hasAttribute("data-compare-price-total")) {
          el.setAttribute("data-compare-price-total", "0");
        }
      }
      else {
        el.setAttribute("data-compare-price", window.formatMoney(comparePrice, window.moneyFormat));
        if (el.hasAttribute("data-compare-price-total")) {
          el.setAttribute("data-compare-price-total", window.formatMoney(comparePrice * state.variants[variantKey].quantity, window.moneyFormat));
        }
      }
    });
  }

  _setDiscount(state: any): void {
    if (!window.mixMatchStore) {
      console.warn("Cannot update discount. Check mixMatchStore");
      return;
    }

    if (this.discountEl) {
      if (state.currentDiscount == 0) {
        this.discountEl.textContent = "";
      }
      else {
        this.discountEl.textContent = `-${state.currentDiscount}%`;
      }
    }
  }

  _removeMiniCard(): void {
    if (this.closest("product-mm-list")) {
      this.remove();
    }
  }

  onStateChange({ action, previousState, state }: { action: string; previousState: any; state: any }): void {
    this._setPrices(state);
    this._setDiscount(state);

    if (action === "UPDATE_VARIANT_QUANTITY") {
      const variantQuantity = state.variants[this.variantKey]?.quantity || 0;
      
      if (this.inputEl) {
        this.inputEl.value = String(variantQuantity);
      }
      this.updateQuantityDisplay();
      this._updateVariantQuantity();

      if (this._getTotalProductQuantity() == 0) {
        this._removeMiniCard();
      }
    }
  }
}

